import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Link, useForm } from '@inertiajs/react';

export function DemoRequestForm() {
  const { data, setData, post, processing, errors, setError, clearErrors } = useForm({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    specialization: '',
    message: '',
    privacy_consent: false,
    _honeypot: '',
    source: 'website',
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!data.privacy_consent) {
      setError('privacy_consent', 'Per procedere, accetta l\'informativa sulla privacy.');
      return;
    }
    post('/demo-request', { preserveScroll: true });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-left">
            {/* Honeypot nascosto */}
            <input
              type="text"
              name="_honeypot"
              value={data._honeypot}
              onChange={(e) => setData('_honeypot', e.target.value)}
              className="hidden"
              tabIndex={-1}
              aria-hidden="true"
            />

            {/* Riga 1: Nome + Cognome */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="first_name">Nome</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  autoComplete="given-name"
                  value={data.first_name}
                  onChange={(e) => setData('first_name', e.target.value)}
                  placeholder="Il tuo nome"
                  required
                  aria-describedby={errors.first_name ? 'first_name_error' : undefined}
                />
                {errors.first_name && (
                  <p id="first_name_error" className="text-destructive text-sm" role="alert">
                    {errors.first_name}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="last_name">Cognome</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  autoComplete="family-name"
                  value={data.last_name}
                  onChange={(e) => setData('last_name', e.target.value)}
                  placeholder="Il tuo cognome"
                  required
                  aria-describedby={errors.last_name ? 'last_name_error' : undefined}
                />
                {errors.last_name && (
                  <p id="last_name_error" className="text-destructive text-sm" role="alert">
                    {errors.last_name}
                  </p>
                )}
              </div>
            </div>

            {/* Riga 2: Email */}
            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                autoComplete="email"
                type="email"
                value={data.email}
                onChange={(e) => setData('email', e.target.value)}
                placeholder="nome@esempio.com"
                required
                aria-describedby={errors.email ? 'email_error' : undefined}
              />
              {errors.email && (
                <p id="email_error" className="text-destructive text-sm" role="alert">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Riga 3: Telefono + Specializzazione */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="phone">Telefono (opzionale)</Label>
                <Input
                  id="phone"
                  name="phone"
                  autoComplete="tel"
                  type="tel"
                  value={data.phone}
                  onChange={(e) => setData('phone', e.target.value)}
                  placeholder="+39 ..."
                  aria-describedby={errors.phone ? 'phone_error' : undefined}
                />
                {errors.phone && (
                  <p id="phone_error" className="text-destructive text-sm" role="alert">
                    {errors.phone}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="specialization">Specializzazione (opzionale)</Label>
                <Input
                  id="specialization"
                  value={data.specialization}
                  onChange={(e) => setData('specialization', e.target.value)}
                  placeholder="Es. Psicoterapia cognitivo-comportamentale"
                  aria-describedby={errors.specialization ? 'specialization_error' : undefined}
                />
                {errors.specialization && (
                  <p id="specialization_error" className="text-destructive text-sm" role="alert">
                    {errors.specialization}
                  </p>
                )}
              </div>
            </div>

            {/* Riga 4: Messaggio */}
            <div className="space-y-1">
              <Label htmlFor="message">Messaggio (opzionale)</Label>
              <Textarea
                id="message"
                value={data.message}
                onChange={(e) => setData('message', e.target.value)}
                placeholder="Raccontaci cosa ti interessa approfondire"
                rows={4}
                aria-describedby={errors.message ? 'message_error' : undefined}
              />
              {errors.message && (
                <p id="message_error" className="text-destructive text-sm" role="alert">
                  {errors.message}
                </p>
              )}
            </div>

            {/* Riga 5: Checkbox privacy */}
            <div className="space-y-1">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy_consent"
                  checked={data.privacy_consent}
                  onCheckedChange={(checked) => {
                    setData('privacy_consent', checked === true);
                    if (checked) clearErrors('privacy_consent');
                  }}
                  className="mt-0.5"
                  aria-describedby={errors.privacy_consent ? 'privacy_error' : undefined}
                />
                <Label
                  htmlFor="privacy_consent"
                  className="text-sm font-normal leading-relaxed cursor-pointer"
                >
                  Ho letto e accetto{' '}
                  <Link href="/privacy-policy" className="underline text-primary">
                    l&apos;informativa sulla privacy
                  </Link>
                  .
                </Label>
              </div>
              {errors.privacy_consent && (
                <p id="privacy_error" className="text-destructive text-sm" role="alert">
                  {errors.privacy_consent}
                </p>
              )}
            </div>

            {/* Submit */}
            <Button type="submit" size="lg" className="w-full text-base h-12" disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  Richiedi la demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
    </form>
  );
}
